import { FilterBar, FilterRow } from "@components/FilterBar";
import GroupInfoBadge from "@components/GroupInfoBadge";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { useChannelsFlat, useObjects } from "@features/devices/api";
import { type DirectoryGroupChannel, useAdminGroups } from "@features/groups/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { useTelemetryGroupTimeseries, useTelemetryTimeseries } from "@features/telemetry/api";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import { Alert, Card, Segmented, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

type DataType = "value" | "energy";
type TelemetryMode = "channel" | "group";

const TelemetryPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];

  const [mode, setMode] = useState<TelemetryMode>("channel");
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [selectedGroup, setSelectedGroup] = useState<string>();
  const [selectedGroupChannel, setSelectedGroupChannel] = useState<number | null>(null);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().subtract(1, "day");
    return { from: start, to: dayjs() };
  });
  const [interval, setInterval] = useState<number>(30);
  const [dataType, setDataType] = useState<DataType>("value");

  const channelsQuery = useChannelsFlat(selectedObject?.objectId);
  const channels = channelsQuery.data ?? [];

  // Загрузка групп для выбранного объекта
  const groupsQuery = useAdminGroups(
    selectedObject
      ? { object: { regionId: selectedObject.regionId, objectId: selectedObject.objectId } }
      : undefined,
  );
  const groups = groupsQuery.data ?? [];

  // Автоматический выбор первого канала
  useEffect(() => {
    if (mode === "channel" && !selectedChannel && channels.length > 0) {
      setSelectedChannel(channels[0].meter_number);
    }
  }, [channels, selectedChannel, mode]);

  // Автоматический выбор первой группы
  useEffect(() => {
    if (mode === "group" && !selectedGroup && groups.length > 0) {
      setSelectedGroup(groups[0].id);
    }
  }, [groups, selectedGroup, mode]);

  const channelOptions = useMemo(() => {
    if (!channels.length) return [];
    return channels.map((ch) => ({
      label: `${ch.meter_number} — ${ch.feeder_name ?? ch.channel_label ?? "канал"}`,
      value: ch.meter_number,
    }));
  }, [channels]);

  const groupOptions = useMemo(() => {
    if (!groups.length) return [];
    return groups.map((g) => ({
      label: `${g.name} (${g.channels?.length ?? 0} каналов)`,
      value: g.id,
    }));
  }, [groups]);

  // Каналы выбранной группы
  const selectedGroupData = useMemo(() => {
    if (!selectedGroup || !groups.length) return null;
    return groups.find((g) => g.id === selectedGroup) ?? null;
  }, [groups, selectedGroup]);

  const groupChannels: DirectoryGroupChannel[] = selectedGroupData?.channels ?? [];

  // Опции для выбора канала внутри группы
  const groupChannelOptions = useMemo(() => {
    if (!groupChannels.length) return [];
    return groupChannels.map((ch) => {
      // Найдём информацию о канале из общего списка каналов
      const channelInfo = channels.find((c) => c.meter_number === ch.channel_id);
      const label = channelInfo
        ? `${ch.channel_id} — ${channelInfo.feeder_name ?? channelInfo.channel_label ?? "канал"}`
        : `Канал ${ch.channel_id}`;
      return {
        label,
        value: ch.channel_id,
        sign: ch.sign,
      };
    });
  }, [groupChannels, channels]);

  // Сбрасываем выбранный канал группы при смене группы
  useEffect(() => {
    setSelectedGroupChannel(null);
  }, []);

  const fromIso = period.from?.toDate().toISOString();
  const toIso = period.to?.toDate().toISOString();

  // Определяем какой канал запрашивать (обычный режим или канал из группы)
  const effectiveChannelID =
    mode === "channel" ? selectedChannel : (selectedGroupChannel ?? undefined);

  // Запрос телеметрии канала (используется в режиме канала или когда выбран канал в группе)
  const channelTelemetryQuery = useTelemetryTimeseries({
    channelID: effectiveChannelID,
    from: fromIso,
    to: toIso,
    interval,
  });

  // Запрос групповой телеметрии (только когда в режиме группы и не выбран конкретный канал)
  const groupTelemetryQuery = useTelemetryGroupTimeseries({
    groupID: mode === "group" && !selectedGroupChannel ? selectedGroup : undefined,
    from: fromIso,
    to: toIso,
    interval,
  });

  // Определяем какой запрос использовать
  const useGroupData = mode === "group" && !selectedGroupChannel;
  const telemetryQuery = useGroupData ? groupTelemetryQuery : channelTelemetryQuery;
  const groupMeta = useGroupData ? groupTelemetryQuery.data?.meta : undefined;

  const chartData = useMemo(() => {
    // Режим канала или выбран конкретный канал в группе
    if (mode === "channel" || selectedGroupChannel) {
      if (!channelTelemetryQuery.data) return [];
      return channelTelemetryQuery.data.map((point) => ({
        time: dayjs(point.timestamp).format("DD.MM HH:mm"),
        value: dataType === "value" ? (point.val ?? 0) : (point.rash_poln ?? 0),
        timestamp: point.timestamp,
      }));
    }
    // Режим группы (агрегированные данные)
    if (!groupTelemetryQuery.data?.data) return [];
    return groupTelemetryQuery.data.data.map((point) => ({
      time: dayjs(point.timestamp).format("DD.MM HH:mm"),
      value: dataType === "value" ? point.sum : point.energy_sum,
      timestamp: point.timestamp,
    }));
  }, [channelTelemetryQuery.data, groupTelemetryQuery.data, dataType, mode, selectedGroupChannel]);

  const dataField = dataType === "value" ? "Значение" : "Энергия (кВт·ч)";
  const ready =
    mode === "channel"
      ? selectedChannel !== undefined && fromIso && toIso
      : selectedGroup !== undefined && fromIso && toIso;

  return (
    <PageLayoutWithHelp title="Телеметрия">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <Segmented
              value={mode}
              onChange={(value) => {
                setMode(value as TelemetryMode);
                setSelectedChannel(undefined);
                setSelectedGroup(undefined);
                setSelectedGroupChannel(null);
              }}
              options={[
                { label: "Канал", value: "channel" },
                { label: "Группа", value: "group" },
              ]}
            />
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => {
                setSelectedObject(value);
                setSelectedChannel(undefined);
                setSelectedGroup(undefined);
              }}
              style={{ minWidth: 280, maxWidth: 400 }}
            />
            {mode === "channel" ? (
              <Select
                showSearch
                placeholder="Канал счётчика"
                disabled={!selectedObject}
                loading={channelsQuery.isLoading}
                options={channelOptions}
                value={selectedChannel}
                onChange={setSelectedChannel}
                optionFilterProp="label"
                style={{ minWidth: 280, maxWidth: 480 }}
              />
            ) : (
              <>
                <Select
                  showSearch
                  placeholder="Группа каналов"
                  disabled={!selectedObject}
                  loading={groupsQuery.isLoading}
                  options={groupOptions}
                  value={selectedGroup}
                  onChange={setSelectedGroup}
                  optionFilterProp="label"
                  style={{ minWidth: 280, maxWidth: 480 }}
                  notFoundContent={
                    groupsQuery.isLoading ? "Загрузка..." : "Нет групп для этого объекта"
                  }
                />
                <Select
                  showSearch
                  allowClear
                  placeholder="каналы группы"
                  disabled={!selectedGroup || groupChannelOptions.length === 0}
                  options={groupChannelOptions}
                  value={selectedGroupChannel}
                  onChange={setSelectedGroupChannel}
                  optionFilterProp="label"
                  style={{ minWidth: 200, maxWidth: 320 }}
                  notFoundContent="Нет каналов в группе"
                />
              </>
            )}
          </FilterRow>

          <FilterRow>
            <Segmented
              value={interval}
              onChange={(value) => setInterval(Number(value))}
              options={[
                { label: "5 мин", value: 5 },
                { label: "15 мин", value: 15 },
                { label: "30 мин", value: 30 },
                { label: "60 мин", value: 60 },
              ]}
            />
            <Segmented
              value={dataType}
              onChange={(value) => setDataType(value as DataType)}
              options={[
                { label: "Значение", value: "value" },
                { label: "Энергия", value: "energy" },
              ]}
            />
          </FilterRow>
          <div style={{ width: "100%" }}>
            <PeriodFilter value={period} onChange={setPeriod} />
          </div>
        </FilterBar>

        {!ready && (
          <Alert
            type="info"
            showIcon
            title={mode === "channel" ? "Выберите объект и канал" : "Выберите объект и группу"}
            description={
              mode === "channel"
                ? "Для отображения графика телеметрии выберите объект и канал из списка выше."
                : "Для отображения агрегированной телеметрии выберите объект и группу каналов."
            }
            style={{ maxWidth: 600 }}
          />
        )}

        {ready && telemetryQuery.isError && (
          <Alert
            type="error"
            showIcon
            title="Ошибка загрузки данных"
            description="Не удалось загрузить данные телеметрии. Проверьте подключение к серверу или попробуйте изменить период."
          />
        )}

        {ready &&
          !telemetryQuery.isError &&
          chartData.length === 0 &&
          !telemetryQuery.isLoading && (
            <Alert
              type="warning"
              showIcon
              title="Нет данных"
              description={
                mode === "channel"
                  ? "За выбранный период данные телеметрии отсутствуют. Попробуйте изменить период или выбрать другой канал."
                  : "За выбранный период данные отсутствуют. Попробуйте изменить период или выбрать другую группу."
              }
            />
          )}

        {ready && chartData.length > 0 && (
          <Card
            title={
              mode === "channel"
                ? `График телеметрии: ${dataField}`
                : selectedGroupChannel
                  ? `Канал ${selectedGroupChannel} из группы «${selectedGroupData?.name ?? ""}»`
                  : `Групповая телеметрия: ${groupMeta?.name ?? ""}${dataType === "energy" ? " (Энергия)" : ""}`
            }
            extra={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Точек: {chartData.length} | Интервал: {interval} мин
              </Typography.Text>
            }
          >
            {groupMeta && !selectedGroupChannel && (
              <GroupInfoBadge
                name={groupMeta.name}
                channelsProcessed={groupMeta.channels_processed}
                channelsTotal={groupMeta.channels_total}
                warning={groupMeta.warning ?? undefined}
              />
            )}
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }} width={80} />
                <Tooltip
                  contentStyle={{ fontSize: 12 }}
                  labelStyle={{ fontWeight: 600 }}
                  formatter={(value: number) => [
                    value.toFixed(3),
                    useGroupData ? `Сумма (${dataField})` : dataField,
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={useGroupData ? "#52c41a" : "#1890ff"}
                  strokeWidth={2}
                  dot={false}
                  name={useGroupData ? `Сумма (${dataField})` : dataField}
                  animationDuration={300}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default TelemetryPage;
